import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Routes, Route, Link, Navigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/Skeleton';
import {
  ArrowLeft,
  Star,
  Settings as SettingsIcon,
  Sparkles,
  FolderKanban,
  Link2,
  BarChart3,
  Eye,
  Trash2,
  Bell,
  StickyNote,
  Target,
  UserPlus,
  MessageSquare,
} from 'lucide-react';
import { RoleBadge } from '../components/RoleSelector';
import InviteMemberDialog from '../components/InviteMemberDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

import ProjectOverviewTab from '../components/ProjectOverviewTab';
import ProjectFeedTab from '../components/ProjectFeedTab';
import ProjectSourcesTab from '../components/ProjectSourcesTab';
import ProjectWatchpointsTab from '../components/ProjectWatchpointsTab';
import ProjectInsightsTab from '../components/ProjectInsightsTab';
import ProjectAlertsTab from '../components/ProjectAlertsTab';
import ProjectNotesTab from '../components/ProjectNotesTab';
import ProjectGoalsTab from '../components/ProjectGoalsTab';
import ProjectSettingsTab from '../components/ProjectSettingsTab';
import ProjectConversationsPage from './ProjectConversationsPage';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const currentTab = window.location.pathname.split('/')[3] || 'overview';

  // User's role in this project (returned from API as user_role)
  const userRole = project?.user_role || 'viewer';
  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'admin' || isOwner;
  const isEditor = userRole === 'editor' || isAdmin;

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await api.get(`/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    setDeleting(true);
    try {
      const response = await api.delete(`/projects/${id}`);
      const counts = response.data.deleted_counts;

      // Build summary message
      const deletedItems = [];
      if (counts.events > 0) deletedItems.push(`${counts.events} events`);
      if (counts.project_sources > 0) deletedItems.push(`${counts.project_sources} sources`);
      if (counts.watchpoints > 0) deletedItems.push(`${counts.watchpoints} watchpoints`);
      if (counts.insights > 0) deletedItems.push(`${counts.insights} insights`);
      if (counts.scan_results > 0) deletedItems.push(`${counts.scan_results} scan results`);
      if (counts.sync_logs > 0) deletedItems.push(`${counts.sync_logs} sync logs`);

      const summary = deletedItems.length > 0
        ? `Deleted: ${deletedItems.join(', ')}`
        : 'Project deleted';

      toast.success('Project deleted successfully', {
        description: summary,
        duration: 5000
      });

      navigate('/projects');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete project';
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!project) return null;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="project-detail-page">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="mb-4"
            data-testid="back-to-projects-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-foreground/90" data-testid="project-name">{project.name}</h1>
                <RoleBadge role={userRole} />
              </div>
              <p className="text-muted-foreground">{project.description}</p>
              <div className="flex items-center gap-2 mt-4">
                <Badge variant="secondary">{project.status}</Badge>
                <span className="text-sm text-muted-foreground">
                  Created {formatDate(project.created_at)}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon">
                <Star className="h-4 w-4" />
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setInviteDialogOpen(true)}
                  title="Invite members"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              )}
              {isEditor && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigate(`/projects/${id}/settings`)}
                  title="Settings"
                >
                  <SettingsIcon className="h-4 w-4" />
                </Button>
              )}
              {isOwner && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid="delete-project-btn"
                  title="Delete project"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={currentTab} onValueChange={(v) => navigate(`/projects/${id}/${v}`)}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">
              <FolderKanban className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="feed" data-testid="tab-feed">
              Feed
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="tab-sources">
              <Link2 className="h-4 w-4 mr-2" />
              Sources
            </TabsTrigger>
            <TabsTrigger value="watchpoints" data-testid="tab-watchpoints">
              <Eye className="h-4 w-4 mr-2" />
              Watchpoints
            </TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">
              <BarChart3 className="h-4 w-4 mr-2" />
              Insights
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">
              <Bell className="h-4 w-4 mr-2" />
              Alerts
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">
              <StickyNote className="h-4 w-4 mr-2" />
              Notes
            </TabsTrigger>
            <TabsTrigger value="goals" data-testid="tab-goals">
              <Target className="h-4 w-4 mr-2" />
              Goals
            </TabsTrigger>
            <TabsTrigger value="conversations" data-testid="tab-conversations">
              <MessageSquare className="h-4 w-4 mr-2" />
              Anchor
            </TabsTrigger>
            {isEditor && (
              <TabsTrigger value="settings" data-testid="tab-settings">
                <SettingsIcon className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {/* Tab Content */}
        <Routes>
          <Route path="/" element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<ProjectOverviewTab project={project} />} />
          <Route path="feed" element={<ProjectFeedTab project={project} />} />
          <Route path="sources" element={<ProjectSourcesTab project={project} onUpdate={loadProject} />} />
          <Route path="watchpoints" element={<ProjectWatchpointsTab project={project} onUpdate={loadProject} />} />
          <Route path="insights" element={<ProjectInsightsTab project={project} />} />
          <Route path="alerts" element={<ProjectAlertsTab project={project} />} />
          <Route path="notes" element={<ProjectNotesTab project={project} />} />
          <Route path="goals" element={<ProjectGoalsTab project={project} />} />
          <Route path="conversations" element={<ProjectConversationsPage projectId={project.id} projectName={project.name} />} />
          <Route path="settings" element={<ProjectSettingsTab project={project} onUpdate={loadProject} />} />
        </Routes>
      </div>

      {/* Delete Project Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{project.name}</strong>?
              </p>
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-destructive">
                <p className="font-medium">This will permanently delete:</p>
                <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                  <li>All events and activity feed data</li>
                  <li>All project sources and configurations</li>
                  <li>All watchpoints and monitoring settings</li>
                  <li>All insights and AI-generated summaries</li>
                  <li>All scan results and sync logs</li>
                </ul>
              </div>
              <p className="text-sm font-medium">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete Project'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        projectId={project?.id}
        projectName={project?.name}
        currentUserRole={userRole}
        onInviteSent={loadProject}
      />
    </AppShell>
  );
}
